import { createContext, useContext, useState } from 'react'

const COOKIE_KEY = 'cookies_accepted'
const PREFS_KEY = 'cookie_prefs'

// Optional (non-essential) cookie categories the manager can toggle.
const DEFAULT_PREFS = { performance: false, targeting: false, unclassified: false }

const ConsentContext = createContext(undefined)

function getCookieValue(name) {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'))
  return match ? match[1] : null
}

function setCookieValue(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`
}

function readPrefs() {
  const raw = getCookieValue(PREFS_KEY)
  if (!raw) return null
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(decodeURIComponent(raw)) }
  } catch {
    return null
  }
}

// Map our cookie categories to Google consent-mode signals.
// Doc: https://developers.google.com/tag-platform/security/concepts/consent-mode
function updateGtagConsent({ performance, targeting }) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: performance ? 'granted' : 'denied',
      ad_storage: targeting ? 'granted' : 'denied',
      ad_user_data: targeting ? 'granted' : 'denied',
      ad_personalization: targeting ? 'granted' : 'denied',
    })
  }
}

export function ConsentProvider({ children }) {
  const [cookiesAccepted, setCookiesAcceptedState] = useState(() => {
    const existing = getCookieValue(COOKIE_KEY)
    if (existing === 'true') return true
    if (existing === 'false') return false
    return undefined
  })
  const [consent, setConsentState] = useState(() => readPrefs() || DEFAULT_PREFS)

  // Persist and apply a granular consent selection (the honest path used by the
  // cookie manager). `cookiesAccepted` stays a simple decided/undecided flag so
  // the banner keeps working unchanged (undefined = not decided → banner shows).
  const applyConsent = (prefs) => {
    const next = { ...DEFAULT_PREFS, ...prefs }
    const anyOptional = next.performance || next.targeting || next.unclassified
    setCookieValue(COOKIE_KEY, String(anyOptional), 365)
    setCookieValue(PREFS_KEY, encodeURIComponent(JSON.stringify(next)), 365)
    setCookiesAcceptedState(anyOptional)
    setConsentState(next)
    updateGtagConsent(next)
  }

  // Back-compat all-or-nothing setter (banner "Accept all").
  const setCookiesAccepted = (value) => {
    applyConsent({ performance: value, targeting: value, unclassified: value })
  }

  return (
    <ConsentContext.Provider value={{ cookiesAccepted, consent, setCookiesAccepted, applyConsent }}>
      {children}
    </ConsentContext.Provider>
  )
}

export function useConsent() {
  const context = useContext(ConsentContext)
  if (context === undefined) {
    throw new Error('useConsent must be used within ConsentProvider')
  }
  return context
}
